import { workspaceDB } from '../core/workspace/workspaceIndexedDB.js';

/**
 * Service tích hợp Invidious API để tìm kiếm và lấy thông tin video YouTube
 * Ưu điểm:
 * 1. Không cần API Key (miễn phí, không bị giới hạn quota gắt gao như Google API)
 * 2. Hỗ trợ CORS sẵn, gọi trực tiếp từ trình duyệt
 * 3. Tự động chuyển đổi dự phòng (failover) giữa các Invidious instance nếu một máy chủ gặp sự cố
 */

export class InvidiousService {
  // Danh sách các Invidious instance cộng đồng có mở API & CORS còn hoạt động tốt
  static INSTANCES = [
    'https://invidious.projectsegfau.lt',
    'https://invidious.perennialte.ch',
    'https://iv.nboeck.de',
    'https://yewtu.be'
  ];

  // Lưu lại instance gần nhất hoạt động tốt để tái sử dụng nhanh
  static activeInstance = 'https://invidious.projectsegfau.lt';

  /**
   * Bóc tách mã videoId YouTube từ URL hoặc chuỗi ký tự bất kỳ
   * @param {string} input Đường dẫn hoặc mã video
   * @returns {string|null} Mã video 11 ký tự hoặc null nếu không hợp lệ
   */
  static extractVideoId(input) {
    // Bước 1: Kiểm tra rỗng và chuẩn hóa dữ liệu đầu vào
    if (!input || typeof input !== 'string') {
      return null;
    }
    const clean = input.trim();
    if (!clean) return null;

    // Bước 2: Bóc tách từ các định dạng liên kết YouTube phổ biến
    // Ví dụ: youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/..., youtube.com/shorts/...
    try {
      const patterns = [
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/
      ];

      for (const regex of patterns) {
        const match = clean.match(regex);
        if (match && match[1]) {
          return match[1];
        }
      }

      // Trường hợp URL có tham số query v=...
      if (clean.includes('v=')) {
        const urlObj = new URL(clean.startsWith('http') ? clean : `https://${clean}`);
        const vParam = urlObj.searchParams.get('v');
        if (vParam && /^[a-zA-Z0-9_-]{11}$/.test(vParam)) {
          return vParam;
        }
      }
    } catch (err) {
      console.warn('Lỗi phân tích cú pháp liên kết YouTube:', err);
    }

    // Bước 3: Nếu người dùng nhập trực tiếp chuỗi videoId 11 ký tự (chứa cả số và chữ, hoặc không chứa khoảng trắng)
    const directIdRegex = /^[a-zA-Z0-9_-]{11}$/;
    if (directIdRegex.test(clean) && !clean.includes(' ') && !clean.includes('.')) {
      return clean;
    }

    return null;
  }

  /**
   * Chuyển đổi số giây thành định dạng thời gian mm:ss hoặc hh:mm:ss
   * @param {number} totalSeconds Tổng số giây
   * @returns {string} Chuỗi hiển thị thời gian
   */
  static formatDuration(totalSeconds) {
    if (!totalSeconds || isNaN(totalSeconds) || totalSeconds < 0) {
      return '00:00';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const pad = (num) => String(num).padStart(2, '0');

    if (hours > 0) {
      return `${hours}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(minutes)}:${pad(seconds)}`;
  }

  /**
   * Chuyển đổi định dạng thời gian (ví dụ "03:45", "1:15:30") thành tổng số giây
   * @param {string} str Chuỗi thời gian
   * @returns {number} Số giây
   */
  static parseDurationToSeconds(str) {
    // 1. Kiểm tra rỗng hoặc không đúng định dạng chuỗi
    if (!str || typeof str !== 'string') return 0;
    const parts = str.trim().split(':').map(p => parseInt(p, 10));
    if (parts.some(isNaN)) return 0;

    // 2. Định dạng hh:mm:ss
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    // 3. Định dạng mm:ss
    else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    // 4. Định dạng giây thuần
    else if (parts.length === 1) {
      return parts[0];
    }
    return 0;
  }

  /**
   * Tìm kiếm danh sách video từ Invidious API kèm cơ chế tự động chuyển máy chủ dự phòng
   * @param {string} query Từ khóa tìm kiếm
   * @param {number} maxResults Số lượng kết quả mong muốn
   * @returns {Promise<Array<{ videoId: string, title: string, author: string, durationText: string, lengthSeconds: number, thumbnail: string, viewCountText: string }>>}
   */
  static async searchVideos(query, maxResults = 20, page = 1) {
    // 1. Kiểm tra dữ liệu đầu vào
    if (!query || typeof query !== 'string' || !query.trim()) {
      return [];
    }

    const keyword = encodeURIComponent(query.trim());
    const pageNumber = Math.max(1, Math.floor(Number(page) || 1));

    // 2. Thử gọi qua endpoint API nội bộ trước để có tốc độ phản hồi nhanh nhất (không bị CORS)
    try {
      const proxyCtrl = new AbortController();
      const proxyTid = setTimeout(() => proxyCtrl.abort(), 3500);
      const proxyResp = await fetch(
        `/api/video-search?q=${keyword}&page=${pageNumber}`,
        { signal: proxyCtrl.signal }
      );
      clearTimeout(proxyTid);
      if (proxyResp.ok) {
        const json = await proxyResp.json();
        if (json && json.success && Array.isArray(json.data) && json.data.length > 0) {
          return this.formatSearchResults(json.data, maxResults);
        }
      }
    } catch (e) {
      // Fallback sang các instance Invidious công cộng
    }

    // 3. Danh sách máy chủ ưu tiên: đưa activeInstance lên đầu
    const candidateInstances = [
      this.activeInstance,
      ...this.INSTANCES.filter(inst => inst !== this.activeInstance)
    ];

    // 4. Thử lần lượt các máy chủ với timeout rút ngắn 3 giây mỗi request
    for (const baseUrl of candidateInstances) {
      try {
        const apiUrl = `${baseUrl}/api/v1/search?q=${keyword}&type=video&page=${pageNumber}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch(apiUrl, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          continue; // Chuyển sang máy chủ tiếp theo
        }

        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          this.activeInstance = baseUrl;
          return this.formatSearchResults(data, maxResults);
        }
      } catch (err) {
        console.warn(`Máy chủ ${baseUrl} không khả dụng, đang thử máy chủ tiếp theo...`);
      }
    }

    return [];
  }

  /**
   * Lấy video liên quan đến video hiện tại từ Invidious API.
   * @param {string} videoId Mã video YouTube hiện tại
   * @param {number} maxResults Số lượng kết quả mong muốn
   * @returns {Promise<Array<{ videoId: string, title: string, author: string, durationText: string, lengthSeconds: number, thumbnail: string, viewCountText: string }>>}
   */
  static async getRelatedVideos(videoId, maxResults = 15, fallbackQuery = '') {
    if (!videoId || typeof videoId !== 'string' || !videoId.trim()) {
      return [];
    }

    const currentVideoId = videoId.trim();
    const cacheLimit = Math.min(15, Math.max(1, Number(maxResults) || 15));
    const cachedItems = await workspaceDB.getRelatedVideoCache(currentVideoId);
    if (Array.isArray(cachedItems)) {
      return cachedItems.slice(0, cacheLimit);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(
        `/api/video-related?v=${encodeURIComponent(currentVideoId)}`,
        { signal: controller.signal }
      );
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error(`API trả về nội dung không phải JSON (HTTP ${response.status})`);
      }
      const json = await response.json();
      if (!response.ok || !json?.success || !Array.isArray(json.data)) {
        throw new Error(json?.message || `HTTP ${response.status}`);
      }

      const items = this.formatSearchResults(
        json.data.filter(item => item && item.type === 'video'),
        cacheLimit
      )
        .filter(item => item.videoId !== currentVideoId);
      if (items.length > 0) {
        await workspaceDB.putRelatedVideoCache(currentVideoId, items);
      }
      return items;
    } catch (err) {
      console.warn('API video liên quan không khả dụng; thử tìm video tương tự:', err);
      if (!fallbackQuery || typeof fallbackQuery !== 'string' || !fallbackQuery.trim()) return [];

      const fallbackItems = (await this.searchVideos(fallbackQuery, cacheLimit + 1))
        .filter(item => item.videoId !== currentVideoId)
        .slice(0, cacheLimit);
      if (fallbackItems.length > 0) {
        await workspaceDB.putRelatedVideoCache(currentVideoId, fallbackItems);
      }
      return fallbackItems;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Chuẩn hóa và làm sạch dữ liệu kết quả tìm kiếm
   * @param {Array<any>} rawItems 
   * @param {number} maxResults 
   * @returns {Array<any>}
   */
  static formatSearchResults(rawItems, maxResults = 20) {
    if (!Array.isArray(rawItems)) return [];

    return rawItems.slice(0, maxResults).map((item, index) => {
      if (!item || typeof item !== 'object') return null;

      const videoId = item.videoId || '';
      if (!videoId) return null;

      // 1. Xử lý thời lượng video linh hoạt giữa chuỗi và số giây
      let durationText = (item.durationText || '').trim();
      let lengthSeconds = Number(item.lengthSeconds) || 0;

      // Nếu có chuỗi thời gian (ví dụ "03:45", "1:15:20") nhưng chưa có số giây -> tự động tính số giây
      if (durationText && lengthSeconds <= 0) {
        lengthSeconds = this.parseDurationToSeconds(durationText);
      }
      // Nếu có số giây nhưng chưa có chuỗi thời gian -> format sang dạng mm:ss hoặc hh:mm:ss
      if (lengthSeconds > 0 && !durationText) {
        durationText = this.formatDuration(lengthSeconds);
      }
      if (!durationText) {
        durationText = '00:00';
      }

      // 2. Chuẩn hóa hình ảnh đại diện thumbnail
      const thumbnail = item.thumbnail || (videoId 
        ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
        : (item.videoThumbnails?.[0]?.url || ''));

      // 3. Chuẩn hóa số lượt xem
      let viewCountText = item.viewCountText || '';
      if (!viewCountText && item.viewCount) {
        viewCountText = `${Number(item.viewCount).toLocaleString('vi-VN')} lượt xem`;
      }

      return {
        index: index + 1,
        videoId: videoId,
        title: item.title || 'Untitled Footage',
        author: item.author || 'Unknown Creator',
        lengthSeconds: lengthSeconds,
        durationText: durationText,
        thumbnail: thumbnail,
        viewCountText: viewCountText,
        publishedText: item.publishedText || ''
      };
    }).filter(item => Boolean(item && item.videoId));
  }

  /**
   * Chuẩn hóa các phiên xem video gần đây thành kết quả tương thích với asset cards.
   * @param {Array<Object>} recentItems
   * @param {number} maxResults
   * @returns {Array<Object>}
   */
  static formatRecentVideos(recentItems, maxResults = 20) {
    if (!Array.isArray(recentItems)) return [];

    const videoItems = recentItems
      .filter(item => item && ['canva', 'capcut'].includes(item.appId))
      .map(item => {
        const videoId = item.videoId || item.contentId || item.position?.videoId || '';
        return {
          videoId,
          title: item.rawTitle || item.title || 'Video đã xem',
          author: item.author || item.position?.author || 'Video đã xem gần đây',
          lengthSeconds: Number(item.duration) || Number(item.position?.duration) || 0,
          thumbnail: item.thumbnail || (videoId
            ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
            : '')
        };
      })
      .filter(item => /^[a-zA-Z0-9_-]{11}$/.test(item.videoId));

    const seenIds = new Set();
    return this.formatSearchResults(videoItems, maxResults)
      .filter(item => {
        if (seenIds.has(item.videoId)) return false;
        seenIds.add(item.videoId);
        return true;
      });
  }

  /**
   * Trả về gợi ý kế tiếp hợp lệ, không bao gồm video hiện đang phát.
   * @param {Array<{videoId: string}>} items
   * @param {string} currentVideoId
   * @returns {Object|null}
   */
  static getNextRelatedVideo(items, currentVideoId) {
    if (!Array.isArray(items)) return null;
    return items.find(item => item?.videoId && item.videoId !== currentVideoId) || null;
  }
}
